import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { evenement } from "@/lib/pixel";
import { useI18n } from "@/i18n/I18nContext";
import LangSwitch from "@/components/LangSwitch";
import ThemeSwitch from "@/components/ThemeSwitch";
import Marque from "@/components/Marque";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Mail } from "lucide-react";

const CONTACT = "quotes@roofborder.com";

/** Retour de paiement Stripe : le compte s'ouvre ici, pas dans un courriel. */
export default function Bienvenue() {
  const { lang } = useI18n();
  const fr = lang !== "en";
  const [etat, setEtat] = useState(null);      // null = en cours
  const [echec, setEchec] = useState(false);
  const essais = useRef(0);

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id") || "";
    if (!sid) { setEchec(true); return; }
    let vivant = true;
    let minuteur;

    // Le compte est ouvert par la page ou par le webhook, selon lequel arrive
    // en premier. On interroge jusqu'à ce que ce soit fait.
    const verifier = async () => {
      try {
        const { data } = await api.get("/public/checkout-status", { params: { session_id: sid } });
        if (!vivant) return;
        if (data?.pret) {
          setEtat(data);
          // Compte réellement ouvert et payé : c'est LA vente. Le serveur envoie
          // le même achat par l'API Conversions ; l'identifiant partagé
          // (`purchase_<session Stripe>`) permet à Meta de n'en compter qu'un.
          evenement("Purchase", {
            content_name: data.plan || "", value: data.montant || 0,
            currency: (data.devise || "cad").toUpperCase(),
          }, `purchase_${sid}`);
          return;
        }
      } catch {
        if (!vivant) return;
      }
      essais.current += 1;
      if (essais.current > 20) { setEchec(true); return; }
      minuteur = setTimeout(verifier, 1500);
    };
    verifier();
    return () => { vivant = false; clearTimeout(minuteur); };
  }, []);

  return (
    <div className="min-h-screen bg-app text-ink">
      <header className="border-b border-line">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Marque taille="md" />
          <div className="flex items-center gap-3">
            <ThemeSwitch />
            <LangSwitch />
          </div>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-6 py-20">
        {!etat && !echec && (
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-brand mx-auto mb-5" />
            <p className="text-ink-soft">
              {fr ? "Nous confirmons votre paiement et ouvrons votre compte…" : "Confirming your payment and opening your account…"}
            </p>
          </div>
        )}

        {echec && (
          <div className="text-center">
            <h1 className="font-heading text-2xl font-extrabold tracking-tighter mb-3">
              {fr ? "Paiement reçu, compte en cours d'ouverture" : "Payment received, account being opened"}
            </h1>
            <p className="text-ink-soft">
              {fr
                ? "Votre paiement est bien passé, mais l'ouverture du compte prend plus de temps que prévu. Vos identifiants arrivent par courriel dans quelques minutes."
                : "Your payment went through, but opening the account is taking longer than expected. Your credentials will arrive by email within a few minutes."}
            </p>
            <a href={`mailto:${CONTACT}`} className="text-brand-ink hover:underline text-sm mt-4 inline-block">
              {CONTACT}
            </a>
          </div>
        )}

        {etat && (
          <div>
            <div className="h-12 w-12 rounded-full bg-brand/15 flex items-center justify-center mb-6">
              <Check className="h-6 w-6 text-brand" />
            </div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tighter leading-tight">
              {fr ? "Bienvenue chez Roof Border." : "Welcome to Roof Border."}
            </h1>
            <p className="mt-4 text-ink-soft">
              {fr
                ? `Votre forfait ${etat.plan_nom} est actif. Votre compte est ouvert au nom de `
                : `Your ${etat.plan_nom_en} plan is active. Your account is open under `}
              <strong className="text-ink">{etat.email}</strong>.
            </p>

            {etat.activation_url ? (
              <>
                <p className="mt-6 text-ink-soft">
                  {fr
                    ? "Il ne reste qu'à choisir votre mot de passe."
                    : "All that's left is to choose your password."}
                </p>
                <a href={etat.activation_url}>
                  <Button className="mt-5 bg-brand hover:bg-brand/90 text-white rounded-md h-12 px-7 text-base font-bold">
                    {fr ? "Choisir mon mot de passe" : "Choose my password"}
                  </Button>
                </a>
                <p className="mt-4 text-sm text-ink-faint flex items-start gap-2">
                  <Mail className="h-4 w-4 shrink-0 mt-0.5" />
                  {fr
                    ? "Le même lien vous a été envoyé par courriel — vous pouvez fermer cette page sans crainte."
                    : "The same link was emailed to you — you can safely close this page."}
                </p>
              </>
            ) : (
              <>
                <p className="mt-6 text-ink-soft">
                  {fr
                    ? "Connectez-vous avec votre mot de passe habituel : votre nouveau forfait est déjà appliqué."
                    : "Sign in with your usual password: your new plan is already applied."}
                </p>
                <Link to="/login">
                  <Button className="mt-5 bg-brand hover:bg-brand/90 text-white rounded-md h-12 px-7 text-base font-bold">
                    {fr ? "Se connecter" : "Sign in"}
                  </Button>
                </Link>
              </>
            )}

            <div className="mt-12 border-t border-line pt-8">
              <h2 className="font-heading font-bold text-lg tracking-tight mb-3">
                {fr ? "Votre première heure" : "Your first hour"}
              </h2>
              <ol className="space-y-2 text-sm text-ink-soft list-decimal pl-5">
                <li>{fr ? "Entrez vos prix et vos matériaux dans Configuration → Prix et produits." : "Enter your prices and materials under Configuration → Pricing and products."}</li>
                <li>{fr ? "Ajoutez votre logo et vos coordonnées : ils apparaissent sur chaque PDF." : "Add your logo and contact details: they appear on every PDF."}</li>
                <li>{fr ? "Faites une soumission sur une maison que vous connaissez, pour valider vos taux." : "Run a quote on a house you know, to check your rates."}</li>
                <li>{fr ? "Collez l'estimateur sur votre site web pour recevoir des prospects." : "Paste the estimator on your website to start getting leads."}</li>
              </ol>
              <p className="text-sm text-ink-faint mt-6">
                {fr ? "Une question ? Écrivez-nous à " : "Any question? Write to us at "}
                <a href={`mailto:${CONTACT}`} className="text-brand-ink hover:underline">{CONTACT}</a>.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
